import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { Chip, Stack } from '@mui/material';
import {
    StyledCellLink,
    Percentage,
    Weight,
} from '../../../components/Common/DataGrid/CellComponents';
import { getRangeFilterOperator } from '../../../components/Common/DataGrid/FilterComponents';
import { select_commonCellProps, text_commonCellProps } from '../../../components/Common/DataGrid/utils';
import { DataGrid } from '../../../components/Common';
import { regulatedAssetTagColors } from '../../../color_utils';
import { fetchFilterValues } from '../../../utils';
import Routes from '../../../routes';

const rangeFilterOperator = getRangeFilterOperator({});

// EPA AIM Act column only applies from 2026-01-01 onward (per ticket 14579 AC).
const AIM_ACT_EFFECTIVE = moment('2026-01-01');
const showAimColumn = moment().isSameOrAfter(AIM_ACT_EFFECTIVE);

const LeakRateCompliance = () => {
    const [filters, setFilters] = useState([]);
    const [allRegulatedAssetTypes, setAllRegulatedAssetTypes] = useState([]);

    useEffect(() => {
        fetchFilterValues(Routes.PowerBI.LeakRateComplianceDropdown())
            .then(setFilters);

        fetch(Routes.RegulatedAssetType.GetAll())
            .then((res) => res.json())
            .then(setAllRegulatedAssetTypes)
            .catch(() => { /* leave empty — chips just render uncolored */ });
    }, []);

    // Columns close over allRegulatedAssetTypes so the regulation chips
    // can resolve name -> id -> color the same way Inventory does.
    const columns = useMemo(() => {
        const cols = [
            {
                field: 'locationName',
                headerName: 'Location',
                ...select_commonCellProps,
                renderCell: ({ row: { locationName, locationId } }) => (
                    <StyledCellLink
                        path={`/Location/${locationId}`}
                        text={locationName}
                        from="Leak Rate Compliance"
                        primaryLink
                    />
                ),
            },
            { field: 'regionName',         headerName: 'Region / Division', initiallyVisible: false, ...text_commonCellProps },
            { field: 'state',              headerName: 'State',             ...select_commonCellProps },
            {
                field: 'assetName',
                headerName: 'Asset Name',
                ...text_commonCellProps,
                renderCell: ({ row: { assetName, assetId } }) => (
                    <StyledCellLink
                        path={`/Inventory/${assetId}`}
                        text={assetName}
                        from="Leak Rate Compliance"
                        primaryLink
                    />
                ),
            },
            { field: 'assetId',            headerName: 'Asset ID',          initiallyVisible: false, ...text_commonCellProps },
            { field: 'assetType',          headerName: 'Asset Type',        ...select_commonCellProps },
            { field: 'assetStatus',        headerName: 'Asset Status',      initiallyVisible: false, ...select_commonCellProps },
            { field: 'operationalStatus',  headerName: 'Operational Status', initiallyVisible: false, ...select_commonCellProps },
            { field: 'materialType',       headerName: 'Material Type',     ...select_commonCellProps },
            {
                field: 'gwp',
                headerName: 'GWP',
                valueGetter: (v) => v,
                filterOperators: rangeFilterOperator,
            },
            {
                field: 'gasCapacityLbs',
                headerName: 'Gas Capacity (lbs)',
                valueGetter: (v) => v,
                filterOperators: rangeFilterOperator,
                renderCell: ({ row: { gasCapacityLbs } }) => <Weight value={gasCapacityLbs} />,
            },
            {
                field: 'currentGasWeightLbs',
                headerName: 'Current Gas Weight (lbs)',
                initiallyVisible: false,
                valueGetter: (v) => v,
                filterOperators: rangeFilterOperator,
                renderCell: ({ row: { currentGasWeightLbs } }) => <Weight value={currentGasWeightLbs} />,
            },
            {
                field: 'calendarYearLeakRate',
                headerName: 'Calendar Year Leak Rate',
                initiallyVisible: false,
                valueGetter: (v) => v,
                filterOperators: rangeFilterOperator,
                renderCell: ({ row: { calendarYearLeakRate } }) =>
                    <Percentage value={calendarYearLeakRate * 100} />,
            },
            {
                field: 'epaAnnualizedLeakRate',
                headerName: 'EPA Annualized Leak Rate',
                initiallyVisible: false,
                valueGetter: (v) => v,
                filterOperators: rangeFilterOperator,
                renderCell: ({ row: { epaAnnualizedLeakRate } }) =>
                    <Percentage value={epaAnnualizedLeakRate * 100} />,
            },
            {
                field: 'epaRollingLeakRate',
                headerName: 'EPA Rolling Average Leak Rate',
                initiallyVisible: false,
                valueGetter: (v) => v,
                filterOperators: rangeFilterOperator,
                renderCell: ({ row: { epaRollingLeakRate } }) =>
                    <Percentage value={epaRollingLeakRate * 100} />,
            },
            {
                field: 'epaRollingClassicLeakRate',
                headerName: 'EPA Rolling Average (Classic) Leak Rate',
                initiallyVisible: false,
                valueGetter: (v) => v,
                filterOperators: rangeFilterOperator,
                renderCell: ({ row: { epaRollingClassicLeakRate } }) =>
                    <Percentage value={epaRollingClassicLeakRate * 100} />,
            },
            {
                field: 'waEcologyRollingLeakRate',
                headerName: 'WA Ecology Rolling Leak Rate',
                initiallyVisible: false,
                valueGetter: (v) => v,
                filterOperators: rangeFilterOperator,
                renderCell: ({ row: { waEcologyRollingLeakRate } }) =>
                    <Percentage value={waEcologyRollingLeakRate * 100} />,
            },
        ];

        if (showAimColumn) {
            cols.push({
                field: 'epaAimActLeakRate',
                headerName: 'EPA AIM Act Leak Rate',
                initiallyVisible: false,
                valueGetter: (v) => v,
                filterOperators: rangeFilterOperator,
                renderCell: ({ row: { epaAimActLeakRate } }) =>
                    <Percentage value={epaAimActLeakRate * 100} />,
            });
        }

        cols.push(
            {
                field: 'applicableRegulations',
                headerName: 'Applicable Regulations',
                sortable: false,
                ...select_commonCellProps,
                renderCell: ({ row: { applicableRegulations } }) => {
                    if (!applicableRegulations) return null;
                    const chips = applicableRegulations
                        .split(',')
                        .map((name) => name.trim())
                        .map((name) => {
                            const found = allRegulatedAssetTypes
                                .find((r) => r.name.toLowerCase() === name.toLowerCase()) || {};
                            return { name, id: found.id, color: regulatedAssetTagColors[found.id] };
                        });
                    return (
                        <Stack direction="row" spacing={1} sx={{ display: 'flex', marginTop: '10px' }}>
                            {chips.map(({ name, id, color }) => (
                                <Chip
                                    key={`${id ?? name}`}
                                    label={name}
                                    sx={{
                                        color,
                                        backgroundColor: '#fff',
                                        border: `1px solid ${color}`,
                                        fontSize: '12px',
                                        height: '28px',
                                    }}
                                />
                            ))}
                        </Stack>
                    );
                },
            },
            {
                field: 'flagStatus',
                headerName: 'Flag Status',
                ...select_commonCellProps,
                renderCell: ({ row: { flagStatus } }) => {
                    if (!flagStatus) return null;
                    // Filled chip: red/green background with white text.
                    // Colors match the existing Pill Error/Success in Pill.css.
                    const color = flagStatus === 'Exceeded' ? 'rgb(224, 2, 2)' : '#3A833C';
                    return (
                        <Chip
                            label={flagStatus}
                            sx={{
                                color: '#fff',
                                backgroundColor: color,
                                border: `1px solid ${color}`,
                                fontSize: '12px',
                                height: '28px',
                            }}
                        />
                    );
                },
            },
            {
                field: 'exceedanceDate',
                headerName: 'Exceedance Date',
                ...text_commonCellProps,
                renderCell: ({ row: { exceedanceDate } }) =>
                    exceedanceDate ? moment(exceedanceDate).format('MM/DD/YYYY') : '',
            },
            {
                field: 'exceedanceServiceEventId',
                headerName: 'Exceedance Service Event',
                initiallyVisible: false,
                ...text_commonCellProps,
                renderCell: ({ row: { exceedanceServiceEventId } }) =>
                    exceedanceServiceEventId
                        ? <StyledCellLink
                            path={`/ServiceEvent/${exceedanceServiceEventId}`}
                            text={exceedanceServiceEventId}
                            from="Leak Rate Compliance"
                            primaryLink
                          />
                        : '',
            },
        );

        return cols;
    }, [allRegulatedAssetTypes]);

    return (
        <DataGrid
            url={Routes.PowerBI.LeakRateCompliance(false)}
            exportButtonURL={Routes.PowerBI.LeakRateCompliance(true)}
            filterOptions={filters}
            columns={columns}
            rowCount={25}
            tableId="leak-rate-compliance-table"
            exportFileName="Leak Rate Compliance"
            exportButtonAlt="Export Leak Rate Compliance (.csv)"
            noResultText="No assets match your filters."
            noResultText2="Adjust your filters or check that assets have an Applicable Regulation tag."
        />
    );
};

export default LeakRateCompliance;
